import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ExternalLink, Loader2, Loader, Play, Plug, RefreshCw, Search, Info,
  Mail, Phone, PhoneCall, MessageCircle, Zap, Workflow, Briefcase,
  Sparkles, MapPin, Bot, Send, CreditCard, FileText, FolderOpen,
  FileSpreadsheet, BarChart3, Mic,
} from "lucide-react";
import Sidebar from "../components/Sidebar";
import ErrorState from "../components/ErrorState";
import LangToggle from "../components/LangToggle";
import ThemeToggle from "../components/ThemeToggle";
import Logo from "../components/Logo";
import IntegrationConfigModal from "../components/IntegrationConfigModal";
import ClaireHelpButton from "../components/ClaireHelpButton";
import { useLang } from "../i18n/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Map backend `icon` string to lucide-react component
const ICON_MAP = {
  Mail, Phone, PhoneCall, MessageCircle, Zap, Workflow, Briefcase, Sparkles,
  MapPin, Bot, Send, CreditCard, FileText, FolderOpen, FileSpreadsheet, BarChart3,
  Mic, Search, Plug,
};

const STATUS_META = {
  connected: { label: "Connected", cls: "bg-[#DCFCE7] text-[#16A34A]" },
  mocked:    { label: "Mocked",    cls: "bg-[#FEF3C7] text-[#B45309]" },
  configure: { label: "Configure", cls: "bg-[#FEE2E2] text-[#DC2626]" },
};

// Order + colour for each category section
const CATEGORY_ORDER = [
  { id: "Communication", color: "#3B82F6", icon: MessageCircle, labelKey: "integ.cat_communication" },
  { id: "Automation",    color: "#F59E0B", icon: Zap,           labelKey: "integ.cat_automation" },
  { id: "CRM",           color: "#A855F7", icon: Briefcase,     labelKey: "integ.cat_crm" },
  { id: "Enrichment",    color: "#22C55E", icon: Sparkles,      labelKey: "integ.cat_enrichment" },
  { id: "Marketing",     color: "#EC4899", icon: Send,          labelKey: "integ.cat_marketing" },
  { id: "Payments",      color: "#10B981", icon: CreditCard,    labelKey: "integ.cat_payments" },
  { id: "Documents",     color: "#0EA5E9", icon: FileText,      labelKey: "integ.cat_documents" },
  { id: "Analytics",     color: "#6366F1", icon: BarChart3,     labelKey: "integ.cat_analytics" },
  { id: "AI",            color: "#8B5CF6", icon: Sparkles,      labelKey: "integ.cat_ai" },
];

export default function Integrations() {
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [executingKey, setExecutingKey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [configModal, setConfigModal] = useState(null);  // integration object

  const STATUS_LABELS = {
    connected: t("integ.status.connected"),
    mocked:    t("integ.status.mocked"),
    configure: t("integ.status.configure"),
  };

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: ig } = await axios.get(`${API}/integrations`);
      setData(ig);
    } catch (e) {
      setError(e?.message || "Failed to load integrations");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const executeIntegration = async (key, name) => {
    setExecutingKey(key);
    try {
      const { data: result } = await axios.post(`${API}/integrations/${key}/execute`);
      toast.success(result.message || `${name} executed`);
      await load();
    } catch (_e) {
      toast.error(t("integ.execute_failed", { name }));
    } finally { setExecutingKey(null); }
  };

  const items = data?.items || [];

  // Apply search + category filter
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((it) => {
      if (activeCategory !== "all" && it.category !== activeCategory) return false;
      if (q && !`${it.name} ${it.category} ${it.desc} ${it.desc_long || ""}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [items, search, activeCategory]);

  // Group filtered by category for "all" view; flat list when category active
  const grouped = useMemo(() => {
    const map = {};
    for (const it of filtered) {
      const cat = it.category || "Other";
      (map[cat] ||= []).push(it);
    }
    return CATEGORY_ORDER
      .map((c) => ({ ...c, items: map[c.id] || [] }))
      .filter((c) => c.items.length > 0);
  }, [filtered]);

  // KPI counters
  const counters = useMemo(() => ({
    total: items.length,
    connected: items.filter((it) => it.status === "connected").length,
    configure: items.filter((it) => it.status === "configure").length,
  }), [items]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--al-bg)] text-[var(--al-text)]">
        <Sidebar />
        <div className="ml-[var(--al-sidebar-w,260px)] flex items-center justify-center h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-[#3B82F6]" />
        </div>
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="min-h-screen bg-[var(--al-bg)] text-[var(--al-text)]">
        <Sidebar />
        <div className="ml-[var(--al-sidebar-w,260px)] px-8 py-16">
          <ErrorState testid="integrations-error" title={t("integ.error_title")} message={error} onRetry={load} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--al-bg)] text-[var(--al-text)]">
      <Sidebar />
      <div className="ml-[var(--al-sidebar-w,260px)]">
        <header
          data-testid="integrations-header"
          className="sticky top-0 z-30 bg-[var(--al-card-bg)] border-b border-[var(--al-border)] px-8 py-4 flex items-center justify-between"
        >
          <div className="flex items-center gap-4">
            <Logo className="w-9 h-9 object-contain" />
            <div>
              <h1 className="text-[16px] font-semibold text-[var(--al-text)]">{t("integ.page_title")}</h1>
              <p className="text-[12.5px] text-[var(--al-text-muted)] mt-0.5">{t("integ.page_subtitle")}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <LangToggle />
            <ClaireHelpButton
              testid="integrations"
              question="Como conecto uma integração (Apify, Hunter, Resend, Stripe) e onde colo a API key?"
            />
            <button
              data-testid="integrations-refresh-btn"
              onClick={load}
              className="flex items-center gap-1.5 px-3 h-9 rounded-lg border border-[var(--al-border)] bg-[var(--al-card-bg)] text-[12.5px] font-medium hover:border-[#3B82F6] hover:text-[#3B82F6]"
            >
              <RefreshCw className="w-3.5 h-3.5" /> {t("dash.refresh")}
            </button>
          </div>
        </header>

        <main className="px-6 lg:px-10 py-6 max-w-[1400px] mx-auto space-y-6">
          {/* Hero / KPI strip */}
          <section data-testid="integ-kpi" className="al-card p-5 flex items-center gap-4 flex-wrap">
            <div className="w-12 h-12 rounded-xl bg-[#EFF6FF] dark:bg-[#1E293B] text-[#3B82F6] flex items-center justify-center shrink-0">
              <Plug className="w-6 h-6" />
            </div>
            <div className="flex-1 min-w-[260px]">
              <h2 className="text-[16px] font-bold text-[var(--al-text)]">{t("integ.hero_title")}</h2>
              <p className="text-[12.5px] text-[var(--al-text-muted)] mt-0.5 max-w-[640px] leading-relaxed">{t("integ.hero_body")}</p>
            </div>
            <div className="flex items-center gap-3">
              <Counter label={t("integ.kpi_total")}     value={counters.total}     color="#3B82F6" />
              <Counter label={t("integ.kpi_connected")} value={counters.connected} color="#16A34A" />
              <Counter label={t("integ.kpi_configure")} value={counters.configure} color="#DC2626" />
            </div>
          </section>

          {/* Search + categories */}
          <section data-testid="integ-filters" className="al-card p-3 sticky top-[84px] z-20">
            <div className="flex items-center gap-2 mb-3">
              <div className="relative flex-1">
                <Search className="w-3.5 h-3.5 text-[var(--al-text-muted)] absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  data-testid="integ-search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t("integ.search_placeholder")}
                  className="al-input h-9 pl-8 text-[12.5px] w-full"
                />
              </div>
              <span className="text-[11.5px] text-[var(--al-text-muted)] shrink-0">
                <strong className="text-[var(--al-text)]">{filtered.length}</strong>/{items.length}
              </span>
            </div>
            {/* Category pills */}
            <div className="flex items-center gap-1.5 flex-wrap" data-testid="integ-categories">
              <CatPill
                id="all"
                active={activeCategory === "all"}
                color="#6B7280"
                Icon={Plug}
                label={t("integ.cat_all")}
                count={items.length}
                onClick={() => setActiveCategory("all")}
              />
              {CATEGORY_ORDER.map((c) => {
                const count = items.filter((it) => it.category === c.id).length;
                if (count === 0) return null;
                return (
                  <CatPill
                    key={c.id}
                    id={c.id}
                    active={activeCategory === c.id}
                    color={c.color}
                    Icon={c.icon}
                    label={t(c.labelKey)}
                    count={count}
                    onClick={() => setActiveCategory(c.id)}
                  />
                );
              })}
            </div>
          </section>

          {/* Grouped sections */}
          {grouped.length === 0 ? (
            <div className="al-card p-10 text-center text-[13px] text-[var(--al-text-muted)]">
              {t("integ.empty_filtered")}
            </div>
          ) : (
            grouped.map((cat) => {
              const CatIcon = cat.icon;
              return (
                <section key={cat.id} data-testid={`integ-cat-section-${cat.id}`}>
                  <header className="flex items-center gap-2 mb-3">
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center"
                      style={{ background: `${cat.color}1A`, color: cat.color }}
                    >
                      <CatIcon className="w-4 h-4" />
                    </div>
                    <h3 className="text-[15px] font-bold text-[var(--al-text)]">{t(cat.labelKey)}</h3>
                    <span className="text-[10.5px] font-semibold uppercase tracking-wider text-[var(--al-text-muted)]">
                      {cat.items.length} {t("integ.cat_count_apps")}
                    </span>
                  </header>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {cat.items.map((it) => (
                      <IntegrationCard
                        key={it.key}
                        it={it}
                        cat={cat}
                        statusLabel={STATUS_LABELS[it.status] || STATUS_META[it.status]?.label}
                        statusCls={STATUS_META[it.status]?.cls || STATUS_META.configure.cls}
                        executing={executingKey === it.key}
                        onExecute={() => executeIntegration(it.key, it.name)}
                        onConfigure={() => setConfigModal(it)}
                      />
                    ))}
                  </div>
                </section>
              );
            })
          )}
        </main>
      </div>

      <IntegrationConfigModal
        open={!!configModal}
        integration={configModal}
        onClose={() => setConfigModal(null)}
        onSaved={async () => { await load(); }}
      />
    </div>
  );
}

function Counter({ label, value, color }) {
  return (
    <div className="text-right">
      <div className="text-[10.5px] uppercase tracking-wider font-semibold text-[var(--al-text-muted)]">{label}</div>
      <div className="text-[20px] font-extrabold tabular-nums" style={{ color }}>{value}</div>
    </div>
  );
}

function CatPill({ id, active, color, Icon, label, count, onClick }) {
  return (
    <button
      type="button"
      data-testid={`integ-cat-${id}`}
      onClick={onClick}
      className={
        "inline-flex items-center gap-1.5 px-2.5 h-7 rounded-full text-[11.5px] font-semibold border transition-colors " +
        (active
          ? "text-white border-transparent"
          : "bg-white text-[var(--al-text-muted)] border-[var(--al-border)] hover:border-[#3B82F6] hover:text-[var(--al-text)]")
      }
      style={active ? { background: color } : undefined}
    >
      <Icon className="w-3 h-3" /> {label}
      <span
        className={
          "inline-flex items-center justify-center min-w-[18px] h-4 px-1 rounded-full text-[9.5px] font-bold " +
          (active ? "bg-white/30 text-white" : "bg-[var(--al-bg)] text-[var(--al-text-muted)]")
        }
      >
        {count}
      </span>
    </button>
  );
}

function IntegrationCard({ it, cat, statusLabel, statusCls, executing, onExecute, onConfigure }) {
  const { t } = useLang();
  const Icon = ICON_MAP[it.icon] || Plug;
  const isConnected = it.status === "connected";
  return (
    <div
      data-testid={`integration-card-${it.key}`}
      className="al-card p-4 flex flex-col h-full hover:border-[#3B82F6] transition-colors"
    >
      <div className="flex items-start gap-3 mb-2">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
          style={{ background: `${cat.color}1A`, color: cat.color }}
        >
          <Icon className="w-5 h-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <h4 className="text-[14px] font-bold text-[var(--al-text)] truncate">{it.name}</h4>
            <a
              href={it.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[var(--al-text-muted)] hover:text-[#3B82F6] shrink-0"
              title={it.url}
            >
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>
          <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[var(--al-text-muted)]">
            {it.category}
          </span>
        </div>
        <span
          data-testid={`integration-status-${it.key}`}
          className={`shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${statusCls}`}
        >
          {statusLabel}
        </span>
      </div>

      <p className="text-[12px] text-[var(--al-text)] leading-snug mb-2 line-clamp-2 font-medium">
        {it.desc}
      </p>
      {it.desc_long && (
        <details className="group mb-3" data-testid={`integration-long-${it.key}`}>
          <summary className="text-[11px] text-[#3B82F6] cursor-pointer list-none inline-flex items-center gap-1 hover:underline">
            <Info className="w-3 h-3" /> {t("integ.read_more")}
          </summary>
          <p className="text-[11.5px] text-[var(--al-text-muted)] mt-1.5 leading-relaxed">
            {it.desc_long}
          </p>
        </details>
      )}

      <div className="mt-auto flex items-center gap-2">
        {isConnected && (
          <button
            type="button"
            data-testid={`integration-execute-${it.key}`}
            disabled={executing}
            onClick={onExecute}
            className="flex-1 al-btn-primary inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-lg text-[11.5px] font-semibold disabled:opacity-60"
          >
            {executing ? <Loader className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
            {t("integ.run")}
          </button>
        )}
        {it.needs_key !== false && (
          <button
            type="button"
            data-testid={`integration-configure-${it.key}`}
            onClick={onConfigure}
            className={
              "inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-lg text-[11.5px] font-semibold transition-colors " +
              (isConnected
                ? "border border-[var(--al-border)] bg-[var(--al-card-bg)] text-[var(--al-text)] hover:border-[#3B82F6] hover:text-[#3B82F6]"
                : "flex-1 al-btn-primary")
            }
          >
            <Plug className="w-3 h-3" />
            {isConnected ? t("integ.update") : t("integ.connect")}
          </button>
        )}
      </div>
    </div>
  );
}
